<template>
  <div class="__table-header">
    <div class="__table-top-info">
      <McAlert v-if="attrs?.alertText" withIcon class="w-100 mb-3" variant="warning">
        <template #label>
          <span v-html="attrs?.alertText" />
        </template>
      </McAlert>
    </div>
    <div v-if="attrs?.headerFilterTopComponent" class="__table-top-filter d-flex align-items-center gap-2 mb-2">
      <FormulateForm ref="formFilter" v-model="formFilter" class="mc-form">
        <div class="d-flex align-items-center gap-2">
          <FormulateInput
            v-for="filter in attrs.headerFilterTopComponent"
            :id="filter.name"
            :key="filter.name"
            v-model="formFilter[filter.name]"
            :name="filter.name"
            :type="filter.type"
            :trackBy="filter.trackBy"
            :placeholder="filter.placeholder"
            :optionsGetter="filter.optionsGetter"
            :optionLabelFn="filter.optionLabelFn"
            :optionLabel="filter.optionLabel"
            :fullObject="filter.fullObject"
            v-bind="
              filter.isGrouping
                ? {
                    'group-values': 'libs',
                    'group-label': 'group',
                  }
                : {}
            "
            class="mc-filter-select"
            @input="handleFilterChange"
            @update:fullObject="($event) => {
              filter.fullObject = $event
              $emit('update:header-filter-full-object', {
                filterName: filter.name,
                value: $event
              })
            }"
          />
        </div>
      </FormulateForm>
      <div class="d-flex gap-2 ms-auto">
        <McButton
          :text="$t('global.apply')"
          @click="applyFilters"
        />
        <McButton
          :text="$t('global.action.clear')"
          variant="secondary"
          @click="resetFilters"
        />
      </div>
    </div>
    <div class="__table-top">
      <div v-if="attrs?.description">
        {{ attrs?.description }}
      </div>
      <h6 v-if="multiple && totalSelected > 0" class="mc-row-check mt-0">
        <span class="badge btn-success">{{ totalSelected }}</span>
        {{ $t('global.object.selectedRow') }}
        <a @click="$emit('unselectAll')" class="color-primary">{{
          $t('global.action.deselect')
        }}</a>
      </h6>
      <!-- TODO: refactor filter button for the next development -->
      <!-- to be able use for api filter -->
      <div v-if="attrs?.filterOptions">
        <ChooseButtonWrapper
          :disabled="isBusy || isEditing"
          :options="attrs.filterOptions.options"
          :value="filterButtonComputed"
          @input="$emit('update:filterButton', $event)"
        />
      </div>

      <FormulateForm ref="form" v-model="form" class="mc-form">
        <div class="d-flex align-items-center gap-2">
          <FormulateInput
            v-for="filter in attrs.headerFilterComponent"
            :id="filter.name"
            :key="filter.name"
            v-model="form[filter.name]"
            :name="filter.name"
            :type="filter.type"
            :trackBy="filter.trackBy"
            :placeholder="filter.placeholder"
            :optionsGetter="filter.optionsGetter"
            :optionLabelFn="filter.optionLabelFn"
            :optionLabel="filter.optionLabel"
            :fullObject="filter.fullObject"
            v-bind="
              filter.isGrouping
                ? {
                    'group-values': 'libs',
                    'group-label': 'group',
                  }
                : {}
            "
            class="mc-filter-select"
            @input="handleFilterChange"
            @update:fullObject="($event) => (filter.fullObject = $event)"
          />
        </div>
      </FormulateForm>

      <McButton
        v-if="attrs.hasAddAction"
        class="mx-2"
        icon="icon-fill_upload"
        :text="$t('global.addField') + ' Audio'"
        @click="$emit('addNewData')"
      />

      <b-input-group
        v-if="attrs.hasSearch == undefined"
        size="sm"
        class="mc-has-search mc-input"
        @click="focusSearch"
      >
        <template #prepend>
          <i class="mc-icon icon-fill_search"></i>
        </template>

        <b-form-input
          ref="searchInput"
          :value="search"
          :disabled="isBusy || isEditing"
          :placeholder="$t('global.search') + '...'"
          @input="handleSearchChange"
        />

        <template #append v-if="search">
          <button
            class="clear-search-btn"
            :disabled="isBusy || isEditing"
            @click="handleClearSearch"
          >
            <i class="pe-2 mc-icon icon-outline_close"></i>
          </button>
        </template>
      </b-input-group>

      <McButton
        v-if="attrs.hasStoreAction"
        class="mx-2"
        icon="icon-fill_plus"
        :disabled="isEditing"
        :text="$t('global.addField')"
        @click="$emit('addNewData')"
      />
    </div>
    <div v-if="attrs?.displayFilterOnly?.isShow" class="mb-3">
      <hr />
      <div class="d-flex align-items-center gap-2">
        <span class="text-nowrap">{{ attrs.displayFilterOnly.name }}: </span>
        <McChipList
          :items="attrs.displayFilterOnly.value"
          :showAllDropdownTitle="attrs.displayFilterOnly.name"
          showAllMode="inline"
          :hideToggle="false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'

import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import McChipList from '@/components/McChipList'
import ChooseButtonWrapper from '../../wrappers/ChooseButtonWrapper'

export default {
  components: {
    McAlert,
    McButton,
    McChipList,
    ChooseButtonWrapper,
  },
  props: {
    attrs: {
      type: Object,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    isBusy: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
    },
    filterButton: {
      type: [String, Number, Boolean],
    },
    totalSelected: {
      type: Number,
    },
  },
  emits: ['update:search', 'update:filterButton', 'unselectAll', 'addNewData', 'filter-change'],
  data() {
    return {
      form: {},
      fullObject: {},
      formFilter: {},
    }
  },
  watch: {
    form: {
      handler: 'handleFilterChange',
      deep: true,
    },
    'attrs.headerFilterComponent': {
      immediate: true,
      handler(newFilters) {
        if (newFilters && Array.isArray(newFilters)) {
          newFilters.forEach((f) => {
            if (typeof this.form[f.name] === 'undefined') {
              this.$set(this.form, f.name, null)
            }
          })
        }
      },
    },
  },
  computed: {
    filterButtonComputed: {
      get() {
        return this.filterButton
      },
      set(value) {
        this.$emit('update:filterButton', value)
      },
    },
  },
  methods: {
    handleSearchChange: debounce(function (_search) {
      this.$emit('update:search', _search)
    }, 500),
    handleClearSearch() {
      this.$emit('update:search', '')
    },
    focusSearch() {
      this.$refs['searchInput'].focus()
    },
    handleFilterChange() {
      this.applyFilters()
    },
    applyFilters() {
      const baseFilters = this.attrs.listGetterFilter || []

      // Handle headerFilterComponent
      const filterData = Object.keys(this.form)
        .filter((key) => {
          const val = this.form[key]
          return val !== null && val !== undefined && val !== ''
        })
        .map((key) => {
          const val = this.form[key]
          const value = typeof val === 'object' ? val.id || val.value : val
          return {
            id: key.includes('select-resourceful-v2_')
              ? key.split('select-resourceful-v2_')[1]
              : key,
            value: value,
          }
        })

      // Handle headerFilterTopComponent
      const topFilterData = Object.keys(this.formFilter)
        .filter((key) => {
          const val = this.formFilter[key]
          return val !== null && val !== undefined && val !== ''
        })
        .map((key) => {
          const val = this.formFilter[key]
          const value = typeof val === 'object' ? val.id || val.value : val
          return {
            id: key.includes('select-resourceful-v2_')
              ? key.split('select-resourceful-v2_')[1]
              : key,
            value: value,
          }
        })

      const combinedFilters = [
        ...baseFilters,
        ...filterData.map((filter) => `${filter.id} eq '${filter.value}'`),
        ...topFilterData.map((filter) => `${filter.id} eq '${filter.value}'`),
      ]

      this.$emit('filter-change', combinedFilters)
    },
    resetFilters() {
      this.formFilter = {}
      this.applyFilters()
    },
  },
}
</script>

<style lang="scss" scoped>
// TODO: Refactor this selector
.mc-input {
  background: $white;
  border: 1px solid $teal-200;
  border-radius: 4px;
  line-height: 16px;
  font-family: $font-family-sans-serif;
  color: #344264;
  font-size: 12px;
  width: 100%;
  justify-content: space-between;

  &.input-md {
    height: 37px;
    padding: 10.5px 10px;
  }

  &.input-sm {
    height: 27px;
    padding: 5.5px 10px;
  }

  & > div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    cursor: pointer;
  }

  input {
    padding: 0px;
  }

  input::placeholder {
    color: $teal-200;
    font-size: 12px;
  }

  i {
    &::before {
      display: block;
      color: #67718b;
      font-size: 20px;
      line-height: none;
    }
  }

  &.__disabled {
    background-color: $gray;
    color: $teal-200;
    cursor: default;

    i::before {
      color: $teal-200;
    }

    input {
      background-color: $gray;
    }
  }
}

.__table-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.mc-has-search {
  padding: 10.5px;
  padding-left: 10px;
  max-width: 30%;
  margin-left: auto;

  // if search is disabled
  &:has(input:disabled) {
    background: $input-disabled-bg;
  }

  .clear-search-btn {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .clear-search-btn:disabled {
    cursor: default;
  }

  input {
    border: none;
    border-radius: 4px;

    padding: 0;
    padding-left: 8px;

    font-size: 12px;
  }

  .input-group-prepend,
  .input-group-append {
    border: none;
    display: flex;
    align-items: center;

    .mc-icon {
      padding-left: 0;
      &:before {
        font-size: 16px;
        color: $input-placeholder-color;
      }
    }
  }
}

.mc-filter-select {
  min-width: 200px;

  :deep(.formulate-input) {
    margin-bottom: 0;
  }
}
</style>
